import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Award, TrendingUp, ChevronDown, ChevronUp,
  ArrowUpRight, ArrowDownRight, Star, DollarSign, ChevronRight
} from 'lucide-react';

import { performanceRankings } from '../data/mockData';

// Card component for the expandable section view
const RankingCardExpandable = ({ title, icon: Icon, rankings }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Icon className="w-5 h-5 text-blue-500" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </div>
      
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="space-y-4">
            {rankings.map((person, index) => (
              <div 
                key={person.id} 
                className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/10 transition"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-semibold">
                  #{index + 1}
                </div>
                <div className="flex-grow min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{person.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {person.region || person.district || person.store}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {typeof person.sales === 'number' 
                      ? `${(person.sales / 1000000).toFixed(1)}M` 
                      : person.sales}
                  </p>
                  <p className={`text-sm ${
                    person.yoyGrowth >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {person.yoyGrowth >= 0 ? '+' : ''}{person.yoyGrowth}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Card component for individual manager cards with photo
const RankingCard = ({ manager, rank }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {rank <= 3 && (
              <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center text-white text-sm font-bold">
                {rank}
              </div>
            )}
            {manager.photo ? (
              <img 
                src={manager.photo} 
                alt={manager.name} 
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-lg font-semibold text-gray-500 dark:text-gray-300">
                {manager.name[0]}
              </span>
            )}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{manager.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{manager.region || manager.district || manager.store}</p>
        </div>
      </div>
      <div className="text-right">
        <div className="font-semibold text-gray-900 dark:text-white">
          {manager.sales.toLocaleString('en-SA', {
            style: 'currency',
            currency: 'SAR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })}
        </div>
        <div className={`flex items-center justify-end text-sm ${
          manager.yoyGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {manager.yoyGrowth >= 0 ? (
            <ArrowUpRight className="w-4 h-4" />
          ) : (
            <ArrowDownRight className="w-4 h-4" />
          )}
          {Math.abs(manager.yoyGrowth)}%
        </div>
      </div>
    </div>
  </motion.div>
);

const RankingsTab = ({ timeRange }) => {
  const [rankingType, setRankingType] = useState('regional'); // regional, district, store
  const [displayMode, setDisplayMode] = useState('cards'); // cards, expandable

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setRankingType('regional')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                rankingType === 'regional' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/10'
              }`}
            >
              Regional Managers
            </button>
            <button
              onClick={() => setRankingType('district')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                rankingType === 'district' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/10'
              }`}
            >
              District Managers
            </button>
            <button
              onClick={() => setRankingType('store')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                rankingType === 'store' 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/10'
              }`}
            >
              Store Managers
            </button>
          </div>
          
          <div className="flex items-center mt-4 sm:mt-0">
            <select 
              className="rounded-lg border border-gray-200 dark:border-gray-700 text-sm p-2 bg-white dark:bg-gray-800"
              value={displayMode}
              onChange={(e) => setDisplayMode(e.target.value)}
            >
              <option value="cards">Card View</option>
              <option value="expandable">Expandable View</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rankings Display */}
      {displayMode === 'cards' ? (
        // Card Grid View
        <div className="grid gap-4">
          {(rankingType === 'regional' ? performanceRankings.regionalManagers :
            rankingType === 'district' ? performanceRankings.districtManagers :
            performanceRankings.storeManagers
          ).map((manager, index) => (
            <RankingCard 
              key={manager.id} 
              manager={manager} 
              rank={index + 1} 
            />
          ))}
        </div>
      ) : (
        // Expandable Sections View
        <div className="space-y-4">
          {rankingType === 'regional' && (
            <RankingCardExpandable 
              title="Regional Manager Rankings" 
              icon={Users} 
              rankings={performanceRankings.regionalManagers || []} 
            />
          )}
          
          {rankingType === 'district' && (
            <RankingCardExpandable 
              title="District Manager Rankings" 
              icon={Award} 
              rankings={performanceRankings.districtManagers || []} 
            />
          )}
          
          {rankingType === 'store' && (
            <RankingCardExpandable 
              title="Store Manager Rankings" 
              icon={TrendingUp} 
              rankings={performanceRankings.storeManagers || []} 
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RankingsTab; 